import { defineStore } from 'pinia'
import { apiService } from '@/services/api'
import { useUserStore } from '@/stores/userStore'

export const useDashboardStore = defineStore('dashboard', {
  state: () => ({
    // 案件進捗データ
    todoSummary: [],

    // 助成金データ
    subsidySummary: [],

    // 電子申請データ
    electronicApplicationSummary: [],

    // 36協定データ
    article36AgreementSummary: [],

    // 業務負荷グラフデータ
    workloadData: [],

    // お知らせデータ
    announcement: '',

    newSystemInfos: [], // 新しいシステムお知らせ
  }),

  actions: {
    // メインダッシュボードデータ取得API呼び出し
    async getDashboardData() {
      try {
        const response = await apiService.get('/api/dashboard')

        // 各カテゴリ別にデータを整理
        this.todoSummary = response.todoSummary || []
        this.subsidySummary = response.subsidySummary || []
        this.electronicApplicationSummary = response.electronicApplicationSummary || []
        this.article36AgreementSummary = response.article36AgreementSummary || []
        this.announcement = response.announcement || ''
        this.newSystemInfos = response.newSystemInfos || []

        return true
      } catch (error) {
        return false
      }
    },

    // 業務負荷グラフデータ取得API呼び出し
    async getWorkloadData(from, to) {
      try {
        // APIが完成したら以下のコメントアウトを外す
        const params = {
          from: from,
          to: to,
        }
        const response = await apiService.get('/api/dashboard/workload', params)

        // APIから取得したデータのキーを日本語に変換
        const convertedData = (response.workloadData || []).map(item => ({
          name: item.name,
          案件: item.todo,
          電子申請: item.electronicApplication,
          助成金: item.subsidy,
          '36協定': item.article36Agreement,
        }))

        this.workloadData = convertedData

        return true
      } catch (error) {
        return false
      }
    },

    // お知らせ更新API呼び出し
    async updateAnnouncement(announcement) {
      try {
        const response = await apiService.post('/api/dashboard/announcement', {
          announcement: announcement,
        })
        this.announcement = announcement
        return response.message
      } catch (error) {
        return false
      }
    },

    // データリセット
    resetData() {
      this.todoSummary = []
      this.subsidySummary = []
      this.electronicApplicationSummary = []
      this.article36AgreementSummary = []
      this.workloadData = []
      this.announcement = ''
      this.newSystemInfos = []
    },

    // 案件進捗データを取得するヘルパー
    getTodoSummaryByType(type) {
      return this.todoSummary.find(item => item.type === type) || { count: 0 }
    },

    // 助成金データを取得するヘルパー（ステータス別）
    getSubsidySummaryByStatus(status) {
      return (
        this.subsidySummary.find(item => item.status === status) || {
          status: status,
          totalCount: 0,
          urgentCount: 0,
          overdueCount: 0,
        }
      )
    },

    // 電子申請データを取得するヘルパー（ステータス別）
    getElectronicApplicationSummaryByStatus(status) {
      return (
        this.electronicApplicationSummary.find(item => item.status === status) || {
          status: status,
          totalCount: 0,
          urgentCount: 0,
          overdueCount: 0,
        }
      )
    },

    // 36協定データを取得するヘルパー（ステータス別）
    getArticle36AgreementSummaryByStatus(status) {
      return (
        this.article36AgreementSummary.find(item => item.status === status) || {
          status: status,
          totalCount: 0,
          urgentCount: 0,
          overdueCount: 0,
        }
      )
    },
  },

  getters: {
    // 案件進捗の種類一覧（todoSummaryから動的生成）
    todoTypes: state => {
      return [...new Set(state.todoSummary.map(item => item.type))]
    },

    // 助成金ステータス一覧
    subsidyStatuses: () => ['', '【計】作成中', '【計】提出完了', '【計】不備対応中', '【支】作成中', '【支】提出完了', '【支】不備対応中', '計画期間中', '確認', '入金連絡有'],

    // 電子申請ステータス一覧
    electronicApplicationStatuses: () => ['', '申請中', '未対応', '確認中', 'その他'],

    // 36協定ステータス一覧
    article36AgreementStatuses: () => ['', '申請中', '確認', '待ち'],

    // ステータス別カラークラス
    colorClass: () => ({
      受付中: 'in-progress',
      計画期間中: 'planning-period',
      '【計】作成中': 'budget-creation-in-progress',
      '【計】提出完了': 'budget-submitted',
      '【計】不備対応中': 'budget-revision-in-progress',
      '【支】作成中': 'payment-creation-in-progress',
      '【支】提出完了': 'payment-submitted',
      '【支】不備対応中': 'payment-revision-in-progress',
      確認: 'awaiting-confirmation',
      入金連絡有: 'payment-received',
      完了: 'completed',
      未完了: 'incomplete',
      申請中: 'application-in-progress',
      未対応: 'unattended',
      確認中: 'under-review',
      その他: 'other',
      待ち: 'pending',
    }),

    // 案件進捗の合計件数
    totalTodoCount: state => {
      return state.todoSummary.reduce((total, item) => total + (item.count || 0), 0)
    },

    // 助成金の合計件数
    totalSubsidyCount: state => {
      return state.subsidySummary.reduce((total, item) => total + (item.totalCount || 0), 0)
    },

    // 電子申請の合計件数
    totalElectronicApplicationCount: state => {
      return state.electronicApplicationSummary.reduce((total, item) => total + (item.totalCount || 0), 0)
    },

    // 36協定の合計件数
    totalArticle36AgreementCount: state => {
      return state.article36AgreementSummary.reduce((total, item) => total + (item.totalCount || 0), 0)
    },

    // 期限間近・超過の合計件数
    urgentItemsCount: state => {
      const subsidyUrgent = state.subsidySummary.reduce((total, item) => total + (item.urgentCount || 0) + (item.overdueCount || 0), 0)

      const electronicUrgent = state.electronicApplicationSummary.reduce((total, item) => total + (item.urgentCount || 0) + (item.overdueCount || 0), 0)

      const article36Urgent = state.article36AgreementSummary.reduce((total, item) => total + (item.urgentCount || 0) + (item.overdueCount || 0), 0)

      return subsidyUrgent + electronicUrgent + article36Urgent
    },

    // データが読み込まれているかどうか
    isDashboardDataLoaded: state => {
      return state.todoSummary.length > 0 || state.subsidySummary.length > 0 || state.electronicApplicationSummary.length > 0 || state.article36AgreementSummary.length > 0
    },

    // 業務負荷データが読み込まれているかどうか
    isWorkloadDataLoaded: state => state.workloadData.length > 0,
  },
})
